import type {
  AutoScoreStudentExamPayload,
  CommonResponseListStudentExamWithExamResponse,
  CommonResponseStudentExamAssignmentResponse,
  GetExamAwardsByData,
  GetExamsWithStudentExamByData,
  GetExamsWithStudentExamByParams,
  GetStudentExamScoringSummaryData,
  GetStudentExamScoringSummaryParams,
  GetStudentExamsWithExamByParams,
  ScoreStudentExamPayload,
  StudentExamAssignmentRequest,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'

import {
  adaptExamAwards,
  adaptExamScoringSummary,
  adaptExamsWithStudentExams,
  adaptStudentExamAssignment,
  adaptStudentExams,
} from './adapters'
import type {
  ExamAward,
  ExamScoringSummary,
  ExamWithStudentExams,
  StudentExam,
  StudentExamAssignment,
  StudentExamWithExam,
} from './schemas'

export type StudentExamListParams = GetStudentExamsWithExamByParams
export type StudentExamAutoScoringPayload = AutoScoreStudentExamPayload
export type StudentExamAssignPayload = StudentExamAssignmentRequest
export type StudentExamScoringPayload = ScoreStudentExamPayload
export type ExamScoringSummaryParams = GetStudentExamScoringSummaryParams
export type ExamOfMonthParams = GetExamsWithStudentExamByParams

export const getStudentExams = async (
  params: StudentExamListParams,
  meta?: RequestMeta,
): Promise<StudentExamWithExam[]> => {
  const body = await httpClient().get<CommonResponseListStudentExamWithExamResponse>(
    '/student-exam',
    { params, meta },
  )
  return adaptStudentExams(body.data) ?? []
}

export const getStudentExamAssignment = async (
  studentExamId: StudentExam['id'],
  meta?: RequestMeta,
): Promise<StudentExamAssignment> => {
  const body = await httpClient().get<CommonResponseStudentExamAssignmentResponse>(
    `/student-exam/${studentExamId}`,
    { meta },
  )
  return adaptStudentExamAssignment(body.data)
}

export const autoScoreStudentExam = async (
  studentExamId: StudentExam['id'],
  payload: StudentExamAutoScoringPayload,
  meta?: RequestMeta,
): Promise<void> => {
  // 학습지·교재와 달리 서버가 version 을 enum 변환 없이 문자열 "v2" 와 그대로 비교한다.
  // 대문자로 보내면 단답 정답의 LaTeX 변환이 빠져 맞는 답도 오답이 될 수 있다.
  await httpClient().patch<unknown>(`/student-exam/${studentExamId}/auto-scoring`, payload, {
    params: { version: 'v2' },
    meta,
  })
}

/** 시험 제출 — 이후 status 가 COMPLETE 로 바뀐다 */
export const solveStudentExam = async (
  studentExamId: StudentExam['id'],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<unknown>(`/student-exam/${studentExamId}/solve`, undefined, { meta })
}

/** 시험을 학생들에게 출제한다 */
export const assignStudentExams = async (
  payload: StudentExamAssignPayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<unknown>('/student-exam', payload, { meta })
}

/** 출제를 취소한다 */
export const unassignStudentExams = async (
  studentExamIds: StudentExam['id'][],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<unknown>('/student-exam', { data: studentExamIds, meta })
}

/** 선생님이 문항별 채점 결과를 고친다 */
export const scoreStudentExam = async (
  studentExamId: StudentExam['id'],
  payload: StudentExamScoringPayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<unknown>(`/student-exam/${studentExamId}/scoring`, payload, { meta })
}

export const getExamScoringSummary = async (
  params: ExamScoringSummaryParams,
  meta?: RequestMeta,
): Promise<ExamScoringSummary> => {
  const body = await httpClient().get<GetStudentExamScoringSummaryData>(
    '/student-exam/scoring-summary',
    { params, meta },
  )
  return adaptExamScoringSummary(body)
}

export const getExamsWithStudentExams = async (
  params: ExamOfMonthParams,
  meta?: RequestMeta,
): Promise<ExamWithStudentExams[]> => {
  const body = await httpClient().get<GetExamsWithStudentExamByData>('/exam/by-year-month', {
    params,
    meta,
  })
  return adaptExamsWithStudentExams(body)
}

export const getExamAwards = async (
  params: ExamOfMonthParams,
  meta?: RequestMeta,
): Promise<ExamAward[]> => {
  const body = await httpClient().get<GetExamAwardsByData>('/exam/award', { params, meta })
  return adaptExamAwards(body)
}
